import {Avatar, Box, Button, Card, Grid, IconButton, Stack, Typography} from "@mui/joy";
import {useSessionCookie} from "../hooks/useSessionCookie";
import React, {useEffect} from "react";
import {Calendar, ChevronRight, Clock, TargetIcon, TrendingUp} from "lucide-react";
import {User} from "../models/user";
import {formatTimeFromSeconds, formatTimeFromSeconds2} from "../utils/formatTime";
import {useAsync} from "../hooks/useAsyncResult";
import {getExamResourceImg, getExams} from "../services/api/exam";
import {useTranslation} from "react-i18next";
import {getTestResults} from "../services/api/session";
import LoadingPageComponent from "../components/Loading/LoadingPageComponent";
import TestResultItemComponent from "../components/Test/TestResultItemComponent";
import {Link} from "react-router-dom";
import LanguageTestSetupDialog from "../components/LanguageTestSetupDialog";
import EmptyDashboardComponent from "../components/EmptyDashboardComponent";
import {appRoute} from "../utils/routes";
import LoadingError from "../components/Error/LoadingError";

const DashboardPage = () => {
    const {userSession} = useSessionCookie()
    const user: User = userSession['currentUser']
    const {t} = useTranslation()
    const examResImgAsyncResult = useAsync(getExamResourceImg, true)
    const testResultsAsyncResult = useAsync(getTestResults, true)

    const loadDashboard = async () => {
        if (user.preferredExam)
            Promise.all([examResImgAsyncResult.execute(user.preferredExam), testResultsAsyncResult.execute(1, 4)])
    }

    useEffect(() => {
        if (user)
            loadDashboard()
    }, [user])


    return <Stack paddingX={4} height={"100%"}>

        {!testResultsAsyncResult.isSuccess && !testResultsAsyncResult.loading &&
            <LoadingError label={t("dashboardSidebarLabel")} onRetry={loadDashboard}/>}

        {testResultsAsyncResult?.loading ?
            <LoadingPageComponent label={t("dashboardSidebarLabel")}/> :
            testResultsAsyncResult.isSuccess &&
            <Stack gap={4} paddingY={3}>
                <Stack gap={2} direction={{sm: "row"}} alignItems={"center"}>
                    <Avatar size={"lg"} sx={{'--Avatar-size': "5rem"}}
                            src={examResImgAsyncResult.payload}/>
                    <Stack>
                        <Typography level={"h2"}>
                            {t("dashboardGreetingTitle", {name: user.name?.split(" ")[0]})}
                        </Typography>
                        <Typography>
                            {t("dashboardGreetingDescription")}
                        </Typography>
                    </Stack>

                </Stack>

                <Grid container spacing={2.5}>
                        <Grid xs={12} md={4}>
                            <Card data-no-animation data-no-glow sx={{gap: 1.5, padding: 2.5}}>
                                <Box borderRadius={12} padding={1} width={"max-content"}
                                     sx={{
                                         display: "flex",
                                         alignItems: "center",
                                         justifyContent: "center",
                                         bgcolor: "primary.softBg",
                                         color: "primary.softColor"
                                     }}>
                                    <TargetIcon style={{color: "inherit", fontSize: 32}}/>
                                </Box>
                                <Typography>
                                    {t("totalTestsLabel")}
                                </Typography>
                                <Typography level={"title-lg"}>
                                    {Object.keys(user?.testsCompleted || 0)?.reduce((previousValue, currentValue) => user.testsCompleted[currentValue] + previousValue, 0) || 0}
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid xs={12} md={4}>
                            <Card data-no-animation data-no-glow sx={{gap: 1.5, padding: 2.5}}>
                                <Box borderRadius={12} padding={1} width={"max-content"}
                                     sx={{
                                         display: "flex",
                                         alignItems: "center",
                                         justifyContent: "center",
                                         bgcolor: "primary.softBg",
                                         color: "primary.softColor"
                                     }}>
                                    <Clock style={{color: "inherit", fontSize: 32}}/>
                                </Box>
                                <Typography>
                                    {t("totalStudyTimeLabel")}
                                </Typography>
                                <Typography level={"title-lg"}>
                                    {formatTimeFromSeconds2(user?.studyTime || 0)}
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid xs={12} md={4}>
                            <Card data-no-animation data-no-glow sx={{gap: 1.5, padding: 2.5}}>
                                <Box borderRadius={12} padding={1} width={"max-content"}
                                     sx={{
                                         display: "flex",
                                         alignItems: "center",
                                         justifyContent: "center",
                                         bgcolor: "primary.softBg",
                                         color: "primary.softColor"
                                     }}>
                                    <TrendingUp style={{color: "inherit", fontSize: 32}}/>
                                </Box>
                                <Typography>
                                    {t("averageScoreLabel")}
                                </Typography>
                                <Typography level={"title-lg"}>
                                    {user?.estimatedScore || 0}
                                </Typography>
                            </Card>
                        </Grid>

                </Grid>

                {testResultsAsyncResult.payload.data?.length === 0 ?
                    <EmptyDashboardComponent/> :
                    <Stack gap={2.5}>
                        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                            <Typography level={"h3"}>
                                {t("recentResultsSectionTitle")}
                            </Typography>

                            <Button endDecorator={<ChevronRight/>} component={Link} variant={"plain"}
                                    to={appRoute("results")}>
                                {t("seeAllResultsActionLabel")}
                            </Button>
                        </Stack>

                        <Grid container spacing={2.5}>
                            {testResultsAsyncResult.payload.data?.map(session =>
                                <Grid xs={12} lg={6} key={session.id ?? session['_id']}>
                                    <TestResultItemComponent session={session} testTitle={session.testTitle}/>
                                </Grid>)}
                        </Grid>
                    </Stack>}

                <Typography level={"body-xs"} textAlign={"center"} fontSize={8}>
                    {t("footerDisclaimer")}
                </Typography>

            </Stack>}

        <LanguageTestSetupDialog onClose={loadDashboard}/>


    </Stack>
}

export default DashboardPage